/**
 * Client knowledge base: "Where do I find this?" step-by-step guides for
 * every connection (founder 2026-07-04: SIMPLE, numbered, verified).
 *
 * Sources: each guide expands the backend registry's `instructions` line,
 * which was written from the platform's official docs at build time
 * (verified 2026-07-02..04). `officialHelp` links go to the platform's own
 * help/developer page — those carry the screenshots we can't take ourselves
 * (we don't have admin accounts on 30+ platforms to screenshot). Steps use
 * the platform's EXACT menu wording wherever it was verified; anything not
 * verified stays at "look for X" phrasing instead of inventing a precise
 * path that might be wrong.
 */

export interface ConnectionGuide {
  /** Numbered, plain-English steps. One action per step. Written for the
   *  NO-login-button case (manual keys) — the fallback that always works. */
  steps: string[];
  /** Steps shown INSTEAD when the platform's login button is actually
   *  rendered (the OAuth app is configured). Never reference "the login
   *  button above" in `steps` — only here. */
  loginSteps?: string[];
  /** The platform's own help/docs page (has their screenshots). */
  officialHelp?: { label: string; url: string };
  /** One honest caveat shown under the steps, when there is one. */
  note?: string;
}

export const CONNECTION_GUIDES: Record<string, ConnectionGuide> = {
  // ── Stores & order sources ──
  ultracart: {
    steps: [
      "Log in to your UltraCart admin.",
      "Go to Configuration → Back Office → API → REST API.",
      "Click to create a Simple Key (if you don't already have one).",
      "Copy the key (it starts with a long string of letters and numbers) and paste it here.",
    ],
    officialHelp: { label: "UltraCart API docs", url: "https://www.ultracart.com/api/" },
  },
  shopify: {
    loginSteps: [
      "In Shopify, go to Settings → Domains and copy the address ending in .myshopify.com. Do not use your public website address.",
      "Paste that .myshopify.com address in the form.",
      "Click “Log in with Shopify” and approve — done. No keys needed.",
    ],
    steps: [
      "In Shopify, go to Settings → Domains and copy the address ending in .myshopify.com. Do not use your public website address.",
      "Go to the Shopify Dev Dashboard at dev.shopify.com (sign in with your normal Shopify login) and create an app.",
      "Give it read access to Orders, Products, and Customers, then install it on your store.",
      "Copy its Client ID and Client Secret and paste them here with your store domain.",
    ],
    note: "Our one-click Shopify app is still waiting on Shopify approval. If you are not the store owner or do not have permission to create apps, skip this for now and ask the store owner to help. Already have an older custom app? You can paste its Admin API access token instead.",
    officialHelp: { label: "Shopify: custom apps guide", url: "https://help.shopify.com/en/manual/apps/app-types/custom-apps" },
  },
  woocommerce: {
    loginSteps: [
      "Type your store's web address in the form.",
      "Click the login button — your own store asks you to approve, no keys needed.",
    ],
    steps: [
      "In WordPress admin go to WooCommerce → Settings → Advanced → REST API.",
      "Click “Add key”, set Permissions to Read, and click “Generate API key”.",
      "Copy the Consumer key and Consumer secret and paste both here.",
    ],
    officialHelp: { label: "WooCommerce: REST API keys", url: "https://woocommerce.com/document/woocommerce-rest-api/" },
  },
  bigcommerce: {
    steps: [
      "In your BigCommerce admin, go to Settings → Store-level API accounts.",
      "Click “Create API account” and choose “V2/V3 API token”.",
      "Set the Orders, Products, and Customers scopes to read-only.",
      "Copy the Access token and Client ID, and note your store hash (the short code in your admin URL, like abc123) — paste all three here.",
    ],
    officialHelp: { label: "BigCommerce: API accounts", url: "https://support.bigcommerce.com/s/article/Store-API-Accounts" },
  },
  clickfunnels: {
    steps: [
      "In ClickFunnels 2.0, click your team name and open Team Settings (note: Team Settings, not Workspace settings).",
      "Click Developer Portal, then “Add new platform application” — that's just ClickFunnels' name for an API connection. Name it anything (e.g. Profit Dashboard) and create it.",
      "Copy the API access token it shows you.",
      "Paste the token here along with your workspace name (the yourname part of yourname.myclickfunnels.com).",
    ],
    officialHelp: { label: "ClickFunnels: getting started with the API", url: "https://developers.myclickfunnels.com/docs/getting-started" },
  },
  kajabi: {
    steps: [
      "In Kajabi, go to Settings → Public API.",
      "Create API credentials — Kajabi gives you a Client ID and Client Secret.",
      "Copy both and paste them here.",
    ],
    note: "Don't see a Public API section under Settings? It only appears on Kajabi's Pro plan (or with the API add-on) — you'd need to upgrade or add it first.",
    officialHelp: { label: "Kajabi developer docs", url: "https://developers.kajabi.com/" },
  },
  konnektive: {
    // Keep in sync with the registry's konnektive instructions (apps/api) —
    // this is the ONE full procedure; the modal intro is one sentence.
    steps: [
      "In Konnektive/Checkout Champ, go to Admin → Users and click Add User.",
      "Set its Access Level to “Api User” — a login for systems, not people. Don't reuse a person's login.",
      "Tick the boxes that let it look up orders and transactions (Konnektive calls these “order/query” and “transactions/query”).",
      "In the IP whitelist, add all four dashboard addresses: 152.55.177.181, 162.220.232.250, 162.220.232.251, and 162.220.232.252. Do not choose AWS.",
      "Paste that user's Login ID and Password here.",
    ],
    note: "All four addresses are permanent and belong to the dashboard. They cover both the connection check and your order imports.",
  },
  buygoods: {
    steps: [
      "Click Connect here first. We will create a private callback link for your account.",
      "In BuyGoods, go to Setup → Products → Settings and open the IPN section.",
      "Paste the same private link into New Order, Rebill, Refund, and Chargeback, then save.",
      "Export your full order history once as a CSV so our team can reconcile the past before approving your numbers.",
    ],
    note: "BuyGoods sends new activity automatically. Its vendor system does not offer a historical order API, so the one-time export is required for past orders.",
    officialHelp: { label: "BuyGoods: IPN setup", url: "https://buygoods.ticksy.com/article/16022/" },
  },
  amazon: {
    steps: [
      "Click the “Request Amazon connection” button in this window.",
      "Your request is saved. Do not wait for a code or keep retrying this screen.",
      "Keep onboarding. Our team will contact the account owner when the Amazon authorization step is ready.",
    ],
    note: "What we can see: orders and finance data only — never your customers' addresses or payment details.",
  },
  hubspot: {
    steps: [
      "In HubSpot, click the Settings gear → Integrations → Private Apps.",
      "Click “Create a private app” and name it “Profit Dashboard”.",
      "On the Scopes tab, add: crm.objects.contacts (Read and Write), crm.objects.orders (Read), and the e-commerce/line items read scope.",
      "Click “Create app”, then copy the access token (starts with pat-) and paste it here.",
    ],
    officialHelp: { label: "HubSpot: private apps", url: "https://developers.hubspot.com/docs/guides/apps/private-apps/overview" },
  },

  // ── Subscription apps ──
  recharge: {
    steps: [
      "In your Recharge admin, go to Tools & apps → API tokens.",
      "Click “Create an API token” and name it “Profit Dashboard.” Under Permissions, set Subscriptions and Orders to Read. Everything else can stay at No access.",
      "Copy the token and paste it here.",
    ],
    note: "Only the Recharge store owner can see API tokens by default. If you cannot open that page, skip it for now and ask the store owner to create the read-only token or grant you access.",
    officialHelp: { label: "Recharge: API tokens", url: "https://docs.getrecharge.com/docs/recharge-api-key" },
  },
  skio: {
    steps: [
      "In your Skio dashboard, go to API & Integrations.",
      "Generate an API token (give it a name like “Profit Dashboard”).",
      "Copy the token and paste it here.",
    ],
    officialHelp: { label: "Skio: using the API", url: "https://help.skio.com/docs/using-the-skio-api" },
  },
  bold_subscriptions: {
    steps: [
      "In Bold's Account Center, find API tokens (under Developer settings).",
      "Create an API access token for Subscriptions.",
      "Copy the token and paste it here — that's all. We detect your shop identifier automatically.",
    ],
    officialHelp: { label: "Bold: API getting started", url: "https://developer.boldcommerce.com/guides/subscriptions-v2/getting-started" },
  },

  // ── Ad platforms (OAuth-only ones get a “just log in” guide) ──
  meta_ads: {
    loginSteps: [
      "Click “Log in with Meta” (Meta is Facebook's parent company — you'll sign in with your normal Facebook account).",
      "Sign in with a Facebook user who can see your ad account (an admin on the Business Manager).",
      "Approve, then pick the ad account if asked — done. No keys needed.",
    ],
    steps: [
      "In Meta Business Settings, go to Users → System Users and create (or open) a system user.",
      "Click “Generate New Token”, pick your ad account, and grant the ads_read permission.",
      "Copy the token, and grab your Ad Account ID from Ads Manager (the number in the account dropdown).",
      "Paste both here.",
    ],
  },
  google_ads: {
    loginSteps: [
      "Click “Log in with Google”.",
      "Sign in with the Google account that can see your Google Ads account.",
      "Approve, then choose the right ad account. No keys needed.",
    ],
    steps: [
      "Google Ads login is not enabled yet. Request the connection here and our team will contact the account owner when it is available.",
    ],
  },
  microsoft_ads: {
    loginSteps: [
      "Click “Connect Your Bing/Microsoft Account”.",
      "Sign in with the username you normally use for Bing or Microsoft Ads.",
      "Approve access, then choose the ad account you want. You do not need to copy or paste anything.",
    ],
    steps: [
      "Microsoft Ads login is not enabled yet. Request the connection here and our team will contact the account owner when it is available.",
    ],
  },
  tiktok_ads: {
    loginSteps: [
      "Click “Log in with TikTok” and sign in with your TikTok for Business account.",
      "Approve — done. No keys needed.",
    ],
    steps: [
      "Click “Request TikTok Ads connection” in this window — that's your only job right now.",
      "We'll email you a connection link (usually same day). Open it and click Approve — about 2 minutes, no keys or settings.",
    ],
    note: "Have developer access to TikTok's Marketing API? Use “I already have an API token” below to paste a token and your Advertiser ID directly.",
  },
  pinterest_ads: {
    loginSteps: [
      "Click “Log in with Pinterest”, sign in with the business account that owns the ads, and approve — done.",
    ],
    steps: [
      "This connection uses a one-click login that we’re finishing enabling on our side — there’s nothing for you to find or paste. The connect window has a button to ask us to turn it on sooner; we’ll email you the moment it’s live.",
    ],
  },
  snapchat_ads: {
    loginSteps: [
      "Click “Log in with Snapchat”, sign in with a Business Manager member, and approve — done.",
    ],
    steps: [
      "This connection uses a one-click login that we’re finishing enabling on our side — there’s nothing for you to find or paste. The connect window has a button to ask us to turn it on sooner; we’ll email you the moment it’s live.",
    ],
  },
  linkedin_ads: {
    loginSteps: [
      "Click “Log in with LinkedIn” and sign in with a user who has access to the ad account.",
      "Approve — done. If login succeeds but no account connects, tell us — LinkedIn requires us to pre-approve each ad account on our side first.",
    ],
    steps: [
      "This connection uses a one-click login that we’re finishing enabling on our side — there’s nothing for you to find or paste. The connect window has a button to ask us to turn it on sooner; we’ll email you the moment it’s live.",
    ],
  },
  amazon_ads: {
    steps: [
      "Click the \u201cRequest Amazon Ads connection\u201d button in this window. That's your only job right now.",
      "We'll email you a link (usually same day). Open it while logged in to the Amazon account that runs your ads.",
      "Click Allow on Amazon's page. Done — about 2 minutes, no keys, no settings.",
    ],
  },
  taboola: {
    // Keep in sync with the registry's taboola instructions (apps/api).
    steps: [
      "Email your Taboola account manager and ask for “Backstage API credentials” (a Client ID and Client Secret) — Taboola issues these directly rather than through a self-serve page.",
      "Your Account ID is visible in Taboola Ads (in the account switcher or the URL, like taboola-yourbrand).",
      "Paste all three here.",
    ],
    note: "Taboola usually replies within a few business days — you can close this window and come back; nothing here is lost.",
  },

  // ── Affiliate & attribution ──
  clickbank: {
    steps: [
      "In ClickBank, open your account and go to the API Management section (under account Settings).",
      "Create an API key with Orders read access.",
      "Copy the full key and paste it here.",
    ],
    officialHelp: { label: "ClickBank API docs", url: "https://api.clickbank.com/rest/1.3/docs" },
  },
  digistore24: {
    steps: [
      "In Digistore24, go to Settings → Account access → API keys.",
      "Create a key with read access.",
      "Copy it and paste it here.",
    ],
    officialHelp: { label: "Digistore24 API docs", url: "https://dev.digistore24.com/" },
  },
  hasoffers: {
    steps: [
      "In your TUNE/HasOffers network admin, go to the API settings.",
      "Copy your Network ID, your Network Token, and the API Domain shown there.",
      "Paste all three here exactly as shown.",
    ],
  },
  everflow: {
    steps: [
      "In your Everflow Control Center, go to Security → API Keys.",
      "Create a new key with reporting access.",
      "Copy it and paste it here.",
    ],
  },
  refersion: {
    steps: [
      "In Refersion, go to your Account settings and find API Keys.",
      "Copy BOTH keys — the Public key and the Secret key.",
      "Paste them here.",
    ],
    officialHelp: { label: "Refersion API docs", url: "https://developers.refersion.com/" },
  },
  impact: {
    steps: [
      "In impact.com, open ⚙ Settings → API (under Technical Settings).",
      "Create a Scoped API Token (or copy an existing one). When Impact asks which permissions to give it, tick READ access for Actions — Actions is Impact's name for your conversions and sales.",
      "Copy your Account SID (starts with IR, shown at the top of the same page) and paste both here.",
    ],
    officialHelp: { label: "impact.com developer docs", url: "https://developer.impact.com/" },
  },
  awin: {
    steps: [
      "In Awin, go to your advertiser account and find the API Credentials page.",
      "Copy the token shown (Awin labels it your “OAuth2 token”) and note your Advertiser ID (shown in your account URL).",
      "Paste both here.",
    ],
    note: "Heads up: Awin only includes API access on Accelerate and Advanced plans — if you can't find an API Credentials page, that's why. Ask your Awin contact to enable it.",
    officialHelp: { label: "Awin API help", url: "https://help.awin.com/" },
  },

  // ── Real costs ──
  stripe_fees: {
    steps: [
      "In your Stripe Dashboard, go to Developers → API keys.",
      "Click “Create restricted key”, name it “Profit Dashboard”, and set Balance Transactions to Read.",
      "Copy the key (starts with rk_live_) and paste it here. (Your regular secret key also works.)",
    ],
    officialHelp: { label: "Stripe: API keys", url: "https://docs.stripe.com/keys" },
  },
  paypal_fees: {
    steps: [
      "Go to developer.paypal.com and log in with your normal PayPal business account.",
      "Open Apps & Credentials, make sure you're on the “Live” tab (not Sandbox), and click “Create App”.",
      "Name it “Profit Dashboard” and create it.",
      "Copy the Client ID and Secret and paste both here.",
    ],
    note: "This makes processing fees exact, but it is not required to finish onboarding. If you are not a PayPal business admin, skip it for now. We will use a clearly labeled starting estimate until an admin connects it.",
    officialHelp: { label: "PayPal: get credentials", url: "https://developer.paypal.com/api/rest/" },
  },
  shipstation: {
    steps: [
      "In ShipStation, click the gear icon → Account → API Settings.",
      "Click “Generate API Keys” if you don't have them yet.",
      "Copy the API Key and API Secret and paste both here.",
    ],
    officialHelp: { label: "ShipStation: API settings", url: "https://help.shipstation.com/hc/en-us/articles/360025856212" },
  },

  // ── Email & SMS ──
  klaviyo: {
    steps: [
      "In Klaviyo, click your account name (bottom left) → Settings → API Keys.",
      "Click “Create Private API Key”. When Klaviyo asks what the key can access, give Full Access to both Lists and Profiles.",
      "Copy the key (starts with pk_) and paste it here.",
    ],
    officialHelp: { label: "Klaviyo: API keys", url: "https://help.klaviyo.com/hc/en-us/articles/115005062267" },
  },
  ontraport: {
    steps: [
      "In Ontraport, go to Administration → Integrations → Ontraport API Instructions & Key Manager.",
      "Create a key with Contacts access.",
      "Copy the App ID AND the API Key and paste both here.",
    ],
    officialHelp: { label: "Ontraport API docs", url: "https://api.ontraport.com/doc/" },
  },
  mailchimp: {
    steps: [
      "In Mailchimp, click your profile icon → Account & billing → Extras → API keys.",
      "Create a key and copy it.",
      "Find your Audience ID under Audience → Settings → “Audience name and defaults”.",
      "Paste both here.",
    ],
    officialHelp: { label: "Mailchimp: API keys", url: "https://mailchimp.com/help/about-api-keys/" },
  },
  attentive: {
    // Keep in sync with the registry's attentive instructions (apps/api).
    steps: [
      "In Attentive, go to Marketplace → Create App (a private app just for your own account).",
      "Set Subscribers to Write. Write includes the read access we need. Set Custom Attributes to Write too. Nothing else is required.",
      "Copy the API key and paste it here.",
    ],
    note: "Attentive is optional. It does not change your dashboard numbers. It only lets you send approved customer segments to Attentive later.",
    officialHelp: { label: "Attentive API docs", url: "https://docs.attentive.com/" },
  },
  postscript: {
    steps: [
      "In Postscript, go to your account settings and find API.",
      "Create a Private API key.",
      "Copy it and paste it here.",
    ],
    officialHelp: { label: "Postscript API docs", url: "https://developers.postscript.io/" },
  },
  gohighlevel: {
    steps: [
      "In GoHighLevel, open the sub-account you want to connect.",
      "Go to Settings → Private Integrations and click “Create new integration”. Give it contacts read/write access, then copy the token it shows you.",
      "Find your Location ID under Settings → Business Info (or it's the string after /location/ in your GoHighLevel web address).",
      "Paste the token and the Location ID here.",
    ],
    note: "We only update contacts that already exist — we never create new contacts.",
    officialHelp: { label: "GoHighLevel: private integrations", url: "https://help.gohighlevel.com/support/solutions/articles/155000004537-private-integrations-api-keys" },
  },
  keap: {
    steps: [
      "Sign in to your Keap Developer account (accounts.infusionsoft.com) and open your app, or in Keap go to Settings → Public API.",
      "Create a Personal Access Token (or a Service Account Key if you're an admin).",
      "Copy the key and paste it here.",
    ],
    note: "We only update contacts that already exist in Keap — we never create new contacts.",
    officialHelp: { label: "Keap: authentication", url: "https://developer.infusionsoft.com/pat-and-sak/" },
  },
  activecampaign: {
    steps: [
      "In ActiveCampaign, click Settings (bottom-left gear) → Developer.",
      "Copy the two values shown: your API URL (like https://youraccount.api-us1.com) and your API Key.",
      "Paste both here.",
    ],
    note: "We only update contacts that already exist in your account — we never create new contacts, and we never touch your deals or pipelines.",
    officialHelp: { label: "ActiveCampaign: API access", url: "https://help.activecampaign.com/hc/en-us/articles/207317590-Getting-started-with-the-API" },
  },
  omnisend: {
    steps: [
      "In Omnisend, go to Store settings → Integrations & API → API keys.",
      "Create an API key. If it asks what the key can access, choose Contacts — with permission to view and update — or just leave the default; that's the only thing we use.",
      "Copy it and paste it here.",
    ],
    officialHelp: { label: "Omnisend API docs", url: "https://api-docs.omnisend.com/" },
  },

  // ── Accounting (OAuth-only) ──
  quickbooks: {
    loginSteps: [
      "Click “Log in with QuickBooks”, sign in with your Intuit account, pick your company, and approve — done. No keys needed.",
    ],
    steps: [
      "This connection uses a one-click login that we’re finishing enabling on our side — there’s nothing for you to find or paste. The connect window has a button to ask us to turn it on sooner; we’ll email you the moment it’s live.",
    ],
  },
  xero: {
    loginSteps: [
      "Click “Log in with Xero”, sign in, pick your organisation, and approve — done. No keys needed.",
    ],
    steps: [
      "This connection uses a one-click login that we’re finishing enabling on our side — there’s nothing for you to find or paste. The connect window has a button to ask us to turn it on sooner; we’ll email you the moment it’s live.",
    ],
  },
  freshbooks: {
    loginSteps: [
      "Click “Log in with FreshBooks”, sign in, and approve — done. No keys needed.",
    ],
    steps: [
      "This connection uses a one-click login that we’re finishing enabling on our side — there’s nothing for you to find or paste. The connect window has a button to ask us to turn it on sooner; we’ll email you the moment it’s live.",
    ],
  },

  // ── Analytics ──
  google_analytics: {
    loginSteps: [
      "First, find your GA4 Property ID: in Google Analytics, click Admin (gear, bottom left) → Property Settings — the Property ID is the number at the top right (like 123456789).",
      "Type it into the form, then click “Log in with Google”.",
      "Sign in with the Google account that can see this Analytics property and approve — done.",
    ],
    steps: [
      "This connection uses a one-click login that we’re finishing enabling on our side — there’s nothing for you to find or paste. The connect window has a button to ask us to turn it on sooner; we’ll email you the moment it’s live.",
    ],
    officialHelp: { label: "Google: find your Property ID", url: "https://support.google.com/analytics/answer/9539598" },
  },
  mixpanel: {
    steps: [
      "In Mixpanel, click the gear icon → Organization Settings → Service Accounts.",
      "Click “Add Service Account” with access to your project, and copy the Username and Secret it shows you (the secret is shown only once).",
      "Find your Project ID under Project Settings → Overview.",
      "Paste all three here. Only set the region field if your Mixpanel project is on EU or India data residency.",
    ],
    officialHelp: { label: "Mixpanel: service accounts", url: "https://docs.mixpanel.com/docs/orgs-and-projects/service-accounts" },
  },
};
